import { FunctionComponent, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faXmark } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import { Select, SelectOption } from 'components/UI/V3/Forms/Select/Select';
import { blockNonInteger, clampInt } from './numericField';
import styles from './DelaySidebar.module.scss';
import { type DelayUnit } from '@spike/automation-model';

export type { DelayUnit };

export interface DelayConfig {
    amount: number;
    unit: DelayUnit;
    extraMinutes?: number;
}

interface Props {
    initialConfig?: DelayConfig;
    onSave?: (config: DelayConfig) => void;
    onCancel?: () => void;
    onConfigChange?: (config: DelayConfig) => void;
}

const UNIT_OPTIONS: SelectOption<never>[] = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' }
];

function buildLabel(amount: number, unit: DelayUnit, extraMinutes?: number): string {
    const unitLabel = unit.charAt(0).toUpperCase() + unit.slice(1);
    const base = `Wait ${amount} ${amount === 1 ? unitLabel.slice(0, -1) : unitLabel}`;
    if (unit === 'hours' && extraMinutes && extraMinutes > 0) {
        return `${base} ${extraMinutes} ${extraMinutes === 1 ? 'Minute' : 'Minutes'}`;
    }
    return base;
}

const DelaySidebar: FunctionComponent<Props> = ({ initialConfig, onSave, onCancel, onConfigChange }) => {
    const [amount, setAmount] = useState(initialConfig?.amount ?? 3);
    const [unit, setUnit] = useState<DelayUnit>(initialConfig?.unit ?? 'hours');
    const [extraMinutes, setExtraMinutes] = useState(initialConfig?.extraMinutes ?? 0);

    useEffect(() => {
        onConfigChange?.({ amount, unit, extraMinutes: unit === 'hours' ? extraMinutes : undefined });
    }, [amount, unit, extraMinutes, onConfigChange]);

    const handleAmountChange = (val: string) => setAmount(clampInt(val, { min: 1, max: 999 }));

    const handleExtraMinutesChange = (val: string) => setExtraMinutes(clampInt(val, { min: 0, max: 59 }));

    const handleSave = () => {
        onSave?.({ amount, unit, extraMinutes: unit === 'hours' ? extraMinutes : undefined });
    };

    return (
        <aside className={styles.sidebar}>
            <div className={styles.body}>
                {/* Header */}
                <div className={styles.nodeHeader}>
                    <div className={styles.iconCircle}>
                        <FontAwesomeIcon icon={faClock} />
                    </div>
                    <div className={styles.headerInfo}>
                        <span className={styles.headerTitle}>Time Delay</span>
                        <span className={styles.headerDesc}>Delay contacts before the next step.</span>
                    </div>
                    {onCancel && (
                        <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={onCancel}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </div>

                {/* Delay config */}
                <div className={styles.section}>
                    <div className={styles.sectionLabel}>Set Time Delay</div>
                    <div className={styles.delayRow}>
                        <input
                            type="number"
                            min={1}
                            max={999}
                            step={1}
                            inputMode="numeric"
                            className={styles.numberInput}
                            onKeyDown={blockNonInteger}
                            onFocus={e => e.target.select()}
                            value={amount}
                            onChange={e => handleAmountChange(e.target.value)}
                        />
                        <Select
                            options={UNIT_OPTIONS}
                            value={unit}
                            onChange={val => setUnit(val as DelayUnit)}
                            className={styles.unitSelect}
                        />
                    </div>

                    {unit === 'hours' && (
                        <div className={styles.section}>
                            <div className={styles.sectionLabel}>And</div>
                            <div className={styles.delayRow}>
                                <input
                                    type="number"
                                    min={0}
                                    max={59}
                                    step={1}
                                    inputMode="numeric"
                                    className={styles.numberInput}
                                    onKeyDown={blockNonInteger}
                                    onFocus={e => e.target.select()}
                                    value={extraMinutes}
                                    onChange={e => handleExtraMinutesChange(e.target.value)}
                                />
                                <span className={styles.minutesLabel}>Minutes</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.footer}>
                <Button
                    label="Save"
                    variant="green"
                    fullWidth
                    onClick={handleSave}
                />
            </div>
        </aside>
    );
};

export default DelaySidebar;
